import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkIcon from "@mui/icons-material/Link";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	Tooltip,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import BlueSkyIcon from "../../icons/react icons/sz-icons/BlueSkyIcon.tsx";
import DiscordIcon from "../../icons/react icons/sz-icons/DiscordIcon.tsx";
import MastodonIcon from "../../icons/react icons/sz-icons/MastodonIcon.tsx";
import MessengerIcon from "../../icons/react icons/sz-icons/MessengerIcon.tsx";
import MSTeamsIcon from "../../icons/react icons/sz-icons/MSTeamsIcon.tsx";
import ThreadsIcon from "../../icons/react icons/sz-icons/ThreadsIcon.tsx";
import { isEvyveska } from "../../../utills/iframeVariants.ts";

declare global {
	interface Window {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		microsoftTeams?: any;
	}
}

type PlatformType =
	| "facebook"
	| "twitter"
	| "email"
	| "linkedin"
	| "messenger"
	| "whatsapp"
	| "instagram"
	| "mastodon"
	| "teams"
	| "discord"
	| "threads"
	| "bluesky";

const isMobileDevice = () => /Mobi|Android/i.test(navigator.userAgent);

const handleShare = (platform: PlatformType, url: string, text: string, handleCopy: () => void) => {
	let shareUrl: string;
	const isMobile = isMobileDevice();
	const encodedUrl = encodeURIComponent(url);
	const encodedText = encodeURIComponent(text);

	switch (platform) {
		case "facebook":
			shareUrl = isMobile
				? `fb://facewebmodal/f?href=${encodedUrl}`
				: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
			break;
		case "twitter":
			shareUrl = isMobile
				? `twitter://post?message=${encodedText}%20${encodedUrl}`
				: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
			break;
		case "email":
			shareUrl = `mailto:?subject=${encodedText}&body=${encodedUrl}`;
			break;
		case "linkedin":
			shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
			break;
		case "messenger":
			shareUrl = isMobile
				? `fb-messenger://share?link=${encodedUrl}`
				: `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=YOUR_APP_ID&redirect_uri=${encodedUrl}`;
			break;
		case "whatsapp":
			shareUrl = isMobile
				? `whatsapp://send?text=${encodedText}%20${encodedUrl}`
				: `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
			break;
		case "teams":
			if (window.microsoftTeams) {
				window.microsoftTeams.sharing.shareWebContent({
					content: [
						{
							type: "URL",
							url: url,
							message: text,
							preview: true,
						},
					],
				});
			} else {
				shareUrl = `https://teams.microsoft.com/share?href=${encodedUrl}&msgText=${encodedText}`;
			}
			break;
		case "mastodon":
			shareUrl = `https://mastodon.social/share?text=${encodedText}%20${encodedUrl}`;
			break;
		case "threads":
			shareUrl = isMobile
				? `barcelona://create?text=${encodedText}%20${encodedUrl}`
				: `https://threads.net/intent/post?text=${encodedText}%20${encodedUrl}`;
			break;
		case "bluesky":
			shareUrl = isMobile
				? `bluesky://intent/compose?text=${encodedText}%20${encodedUrl}`
				: `https://bsky.app/intent/compose?text=${encodedText}%20${encodedUrl}`;
			break;
		default:
			shareUrl = "";
	}

	// @ts-expect-error
	if (shareUrl) {
		window.open(shareUrl, "_blank");
	}

	handleCopy();
};

type ShareButtonsProps = {
	url: string;
	prefilledText: string;
	onCopy: () => void;
	excludedButtons?: PlatformType[];
	customPrefilledText?: Partial<Record<PlatformType, string>>;
	customTooltipText?: Partial<Record<PlatformType, string>>;
	contextMenuButtons?: PlatformType[];
	customCopy?: () => void;
};

const ShareButtons: React.FC<ShareButtonsProps> = ({
	url,
	prefilledText,
	onCopy,
	excludedButtons = [],
	customPrefilledText = {},
	customTooltipText = {},
	contextMenuButtons = [],
	customCopy,
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogContent, setDialogContent] = useState("");
	const open = Boolean(anchorEl);

	useEffect(() => {
		if (!window.microsoftTeams) {
			const script = document.createElement("script");
			script.src = "https://unpkg.com/@microsoft/teams-js@1.11.0-beta.7/dist/MicrosoftTeams.min.js";
			script.async = true;
			script.defer = true;
			document.body.appendChild(script);
			script.onload = () => {
				if (window.microsoftTeams) {
					window.microsoftTeams.initialize();
				}
			};

			return () => {
				document.body.removeChild(script);
			};
		}
	}, []);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const buttons: { platform: PlatformType; icon: React.ReactElement; defaultTooltip: string }[] = [
		{ platform: "facebook", icon: <FacebookIcon color="secondary" />, defaultTooltip: "Sdílet na Facebooku" },
		{ platform: "twitter", icon: <TwitterIcon color="info" />, defaultTooltip: "Sdílet na Twitteru" },
		{ platform: "email", icon: <EmailIcon color="warning" />, defaultTooltip: "Sdílet e-mailem" },
		{ platform: "linkedin", icon: <LinkedInIcon color="secondary" />, defaultTooltip: "Sdílet na LinkedIn" },
		{ platform: "messenger", icon: <MessengerIcon color="secondary" />, defaultTooltip: "Sdílet na Messengeru" },
		{ platform: "whatsapp", icon: <WhatsAppIcon color="success" />, defaultTooltip: "Sdílet na WhatsAppu" },
		{ platform: "instagram", icon: <InstagramIcon color="error" />, defaultTooltip: "Sdílet na Instagramu" },
		{ platform: "mastodon", icon: <MastodonIcon color="info" />, defaultTooltip: "Sdílet na Mastodonu" },
		{ platform: "teams", icon: <MSTeamsIcon />, defaultTooltip: "Sdílet na Teams" },
		{ platform: "discord", icon: <DiscordIcon />, defaultTooltip: "Sdílet na Discordu" },
		{ platform: "threads", icon: <ThreadsIcon />, defaultTooltip: "Sdílet na Threads" },
		{ platform: "bluesky", icon: <BlueSkyIcon color="info" />, defaultTooltip: "Sdílet na Bluesky" },
	];

	const contextButtons = buttons.filter(({ platform }) => contextMenuButtons.includes(platform));
	const visibleButtons = buttons.filter(
		({ platform }) => !excludedButtons.includes(platform) && !contextMenuButtons.includes(platform),
	);

	const handleCopy = customCopy
		? customCopy
		: () => {
				navigator.clipboard
					.writeText(url)
					.then(onCopy)
					.catch(() => {
						setDialogContent(url);
						setDialogOpen(true);
					});
			};

  if(isEvyveska()) { // not necessary do there too, but just in case
    return null;
  }

	return (
		<>
			<Stack direction="row" spacing={1} justifyContent="center" sx={{ margin: "1rem 0" }}>
				{visibleButtons.map(({ platform, icon, defaultTooltip }) => (
					<Tooltip arrow={true} key={platform} title={customTooltipText[platform] || defaultTooltip}>
						<IconButton
							onClick={() => handleShare(platform, url, customPrefilledText[platform] || prefilledText, handleCopy)}
						>
							{icon}
						</IconButton>
					</Tooltip>
				))}
				<Tooltip arrow={true} title="Kopírovat odkaz">
					<IconButton onClick={handleCopy}>
						<LinkIcon />
					</IconButton>
				</Tooltip>
				{contextButtons.length > 0 && (
					<>
						<Tooltip arrow={true} title="Další možnosti sdílení">
							<IconButton onClick={handleClick}>
								<MoreVertIcon />
							</IconButton>
						</Tooltip>
						<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
							{contextButtons.map(({ platform, icon, defaultTooltip }) => (
								<MenuItem
									key={platform}
									onClick={() => handleShare(platform, url, customPrefilledText[platform] || prefilledText, handleCopy)}
								>
									<ListItemIcon>{icon}</ListItemIcon>
									<ListItemText primary={customTooltipText[platform] || defaultTooltip} />
								</MenuItem>
							))}
						</Menu>
					</>
				)}
			</Stack>
			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
				<DialogTitle>Problém s kopírováním odkazu</DialogTitle>
				<DialogContent>
					<DialogContentText>Sdílení selhalo, zkopírujte prosím tento odkaz ručně:</DialogContentText>
					<DialogContentText>
						<a href={dialogContent} target="_blank" rel="noopener noreferrer">
							{dialogContent}
						</a>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)} color="primary">
						Zavřít
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ShareButtons;
